import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { partnerBySlug } from "@/data/partners";
import { PartnerCTA, PartnerDisclosure } from "@/components/site/PartnerBlocks";
import { WhyYouNeedIt, HowItWorks } from "@/components/site/BusinessSetupBlocks";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import heroLandlordInsurance from "@/assets/hero/hero-landlord-insurance.jpg.asset.json";

const HERO = {
  src: heroLandlordInsurance.url,
  alt: "Insurance advisor in a purple blazer reviewing a landlord policy binder with an investor client in a modern office with a city skyline outside",
};

export const Route = createFileRoute("/business-setup/landlord-insurance")({
  head: () =>
    buildHead({
      title: "Landlord Insurance for Investment Property | Simply Approved",
      description:
        "Landlord and rental property insurance for investors, including short-term rental and renovation coverage, with the mortgagee clause lenders require at closing.",
      url: "/business-setup/landlord-insurance",
      type: "article",
      keywords:
        "landlord insurance for investment property, rental property insurance quote, short-term rental insurance coverage, loss of rents insurance, mortgagee clause landlord policy, builder's risk insurance rehab, vacant property insurance investor, DSCR loan insurance requirements, landlord liability insurance, insurance for LLC-owned rental, flood insurance rental property",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Business Setup", url: "/business-setup" },
        { name: "Landlord insurance", url: "/business-setup/landlord-insurance" },
      ],
      service: { name: "Landlord Insurance for Investment Property" },
      image: HERO.src,
      imageAlt: HERO.alt,
    }),
  component: InsurancePage,
});

function InsurancePage() {
  const partner = partnerBySlug("steadily")!;
  return (
    <SiteShell>
      <PageHero
        eyebrow="Business Setup"
        title="Landlord insurance for your rental"
        subtitle="Every closing needs evidence of insurance naming the lender. Investor policies also differ from owner-occupied homeowners coverage."
        image={HERO}
      />
      <PageEssentials />
      <WhyYouNeedIt
        service={"landlord insurance"}
        intro={"No lender funds a rental without evidence of insurance naming them on the policy \u2014 and standard homeowners coverage does not apply to a tenant-occupied or nightly-rental property. Investor policies cover what actually goes wrong on a rental: tenant liability, loss of rents, vacancy during rehab, and short-term rental use."}
        reasons={[
          { title: "Closing cannot happen without it", body: "We need a binder or evidence of insurance with the correct mortgagee clause and loan number before the file can fund." },
          { title: "Homeowners policies exclude rental use", body: "A carrier can deny a claim on a tenant-occupied or nightly-rental property written under an owner-occupied policy." },
          { title: "Loss of rents keeps the debt service covered", body: "If the unit is uninhabitable after a covered loss, loss-of-rents coverage keeps paying the mortgage while it is repaired." },
          { title: "Renovation and vacancy need different coverage", body: "Properties under rehab or sitting vacant typically need builder's risk or a vacancy endorsement, not a standard landlord policy." },
        ]}
      />
      <Section>
        <SectionHeader eyebrow="What closings require" title="Coverage points to confirm with your agent" />
        <ul className="mt-6 grid gap-3 text-sm md:grid-cols-2">
          {[
            "Named insured matching the entity that will hold title",
            "Dwelling coverage at or above the required replacement basis",
            "Loss of rents / rental income coverage",
            "Liability limits meeting the loan requirements",
            "Mortgagee clause and loan number on the evidence of insurance",
            "Vacant or builder's risk coverage during renovation, where applicable",
            "Flood coverage where the property sits in a mapped flood zone",
            "Short-term rental endorsement for nightly-rental properties",
          ].map((i) => (
            <li key={i} className="rounded-lg border border-border bg-card p-4">{i}</li>
          ))}
        </ul>
        <HowItWorks
          steps={[
            "Get a quote for the property address, entity name, and intended use (long-term, Section 8, or short-term rental).",
            "Match the named insured to the entity that will hold title, and confirm liability and loss-of-rents limits.",
            "Send the binder with our mortgagee clause and loan number to your loan officer before closing.",
          ]}
        />
        <PartnerCTA partner={partner} />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/apply" className="rounded-md bg-brand-gold px-5 py-3 text-sm font-semibold text-primary-foreground">Start My Approval</Link>
          <Link to="/dscr-loans" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">DSCR loan program</Link>
        </div>
        <PartnerDisclosure />
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
